import React from 'react';
import { CheckCircle2, ExternalLink, MessageCircleMore, Unplug } from 'lucide-react';

type WhatsAppWizardStep = {
  title: string;
  status: string;
  description: string;
};

type WhatsAppSettingsSectionProps = {
  whatsAppFeedback: { type: 'success' | 'error'; message: string } | null;
  whatsAppIsConnected: boolean;
  whatsAppIsConfigured: boolean;
  whatsAppHasOutboundTest: boolean;
  whatsAppHasInboundTest: boolean;
  whatsAppWizardSteps: WhatsAppWizardStep[];
  whatsAppManualLink: string;
  whatsAppLoading: boolean;
  whatsAppIntegration: any;
  whatsAppPlatformConfig: any;
  whatsAppConnecting: boolean;
  whatsAppDisconnecting: boolean;
  whatsAppTesting: boolean;
  whatsAppInboxLoading: boolean;
  whatsAppTestForm: { to: string; text: string };
  whatsAppMessageLogs: any[];
  setWhatsAppTestForm: React.Dispatch<React.SetStateAction<{ to: string; text: string }>>;
  openWhatsAppEmbeddedSignup: () => void | Promise<void>;
  disconnectWhatsApp: () => void | Promise<void>;
  testWhatsAppConnection: () => void | Promise<void>;
  refreshWhatsAppInbox: () => void | Promise<void>;
};

const stepLabel = (status: string) => {
  if (status === 'done') return 'Concluido';
  if (status === 'current') return 'Proximo';
  if (status === 'blocked') return 'Bloqueado';
  return 'Manual';
};

const stepClass = (status: string) => {
  if (status === 'done') return 'border-emerald-500/30 bg-emerald-500/10';
  if (status === 'current') return 'border-primary/40 bg-primary/10';
  if (status === 'blocked') return 'border-red-500/30 bg-red-500/10';
  return 'border-[#2A2A2A] bg-[#151515]';
};

const WhatsAppSettingsSection: React.FC<WhatsAppSettingsSectionProps> = ({
  whatsAppFeedback,
  whatsAppIsConnected,
  whatsAppIsConfigured,
  whatsAppHasOutboundTest,
  whatsAppHasInboundTest,
  whatsAppWizardSteps,
  whatsAppManualLink,
  whatsAppLoading,
  whatsAppIntegration,
  whatsAppPlatformConfig,
  whatsAppConnecting,
  whatsAppDisconnecting,
  whatsAppTesting,
  whatsAppInboxLoading,
  whatsAppTestForm,
  whatsAppMessageLogs,
  setWhatsAppTestForm,
  openWhatsAppEmbeddedSignup,
  disconnectWhatsApp,
  testWhatsAppConnection,
  refreshWhatsAppInbox,
}) => {
  return (
    <div id="secao-integracoes" className="card w-full scroll-mt-24 space-y-6 p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Integracoes</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Conecte canais externos da propria empresa. O WhatsApp Business usa a identidade e as credenciais do restaurante.
          </p>
        </div>
        <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
          whatsAppIsConnected ? 'bg-emerald-500/15 text-emerald-300' : 'bg-yellow-500/15 text-yellow-300'
        }`}>
          {whatsAppIsConnected ? 'WhatsApp conectado' : 'Aguardando conexao'}
        </span>
      </div>

      <div className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-5">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15">
            <MessageCircleMore className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white">WhatsApp Business</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Use o Embedded Signup oficial da Meta para conectar o numero WhatsApp Business do proprio restaurante.
            </p>
          </div>
        </div>

        {whatsAppFeedback && (
          <div
            className={`mb-5 flex items-start gap-3 rounded-2xl border p-4 text-sm ${
              whatsAppFeedback.type === 'success'
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                : 'border-red-500/30 bg-red-500/10 text-red-200'
            }`}
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4" />
            <span>{whatsAppFeedback.message}</span>
          </div>
        )}

        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h4 className="text-base font-bold text-white">Assistente de conexao do WhatsApp</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Siga as etapas abaixo com o dono do restaurante. O fluxo oficial usa a Meta; se travar, use o fallback manual por link.
              </p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
              whatsAppIsConnected ? 'bg-emerald-500/15 text-emerald-300' : 'bg-yellow-500/15 text-yellow-300'
            }`}>
              {whatsAppIsConnected ? 'Integracao conectada' : 'Aguardando conexao'}
            </span>
          </div>

          <div className="mt-5 grid gap-3 xl:grid-cols-4">
            {whatsAppWizardSteps.map((step, index) => (
              <div key={step.title} className={`rounded-2xl border p-4 ${stepClass(step.status)}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/30 text-xs font-bold text-white">
                    {index + 1}
                  </span>
                  <span className="rounded-full bg-black/25 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {stepLabel(step.status)}
                  </span>
                </div>
                <p className="mt-3 text-sm font-bold text-white">{step.title}</p>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-3">
            <div className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4">
              <h5 className="text-sm font-bold text-white">Diagnostico rapido</h5>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Configuracao Meta</span>
                  <span className={whatsAppIsConfigured ? 'font-semibold text-emerald-300' : 'font-semibold text-red-300'}>
                    {whatsAppIsConfigured ? 'Pronta' : 'Incompleta'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Conta conectada</span>
                  <span className={whatsAppIsConnected ? 'font-semibold text-emerald-300' : 'font-semibold text-yellow-300'}>
                    {whatsAppIsConnected ? 'Sim' : 'Nao'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Webhook</span>
                  <span className={whatsAppIntegration?.webhookVerified ? 'font-semibold text-emerald-300' : 'font-semibold text-yellow-300'}>
                    {whatsAppIntegration?.webhookVerified ? 'Verificado' : 'Pendente'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Envio testado</span>
                  <span className={whatsAppHasOutboundTest ? 'font-semibold text-emerald-300' : 'font-semibold text-yellow-300'}>
                    {whatsAppHasOutboundTest ? 'Sim' : 'Nao'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Recebimento testado</span>
                  <span className={whatsAppHasInboundTest ? 'font-semibold text-emerald-300' : 'font-semibold text-yellow-300'}>
                    {whatsAppHasInboundTest ? 'Sim' : 'Nao'}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4">
              <h5 className="text-sm font-bold text-white">Checklist antes de conectar</h5>
              <ul className="mt-3 space-y-2 text-sm leading-6 text-muted-foreground">
                <li>1. Conta Facebook do dono ou responsavel disponivel.</li>
                <li>2. Numero com acesso a SMS ou ligacao para verificacao.</li>
                <li>3. Numero fora de outro WhatsApp App ou provedor Cloud API.</li>
                <li>4. Dados basicos da empresa prontos para o fluxo da Meta.</li>
              </ul>
            </div>

            <div className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4">
              <h5 className="text-sm font-bold text-white">Fallback manual por link</h5>
              <p className="mt-1 text-sm text-muted-foreground">
                Enquanto a Cloud API nao estiver pronta, abra o WhatsApp com uma mensagem pronta.
              </p>
              <a
                href={whatsAppManualLink || undefined}
                target="_blank"
                rel="noreferrer"
                className={`mt-4 inline-flex rounded-xl border px-4 py-3 text-sm font-semibold transition ${
                  whatsAppManualLink
                    ? 'border-primary/50 text-white hover:bg-primary/10'
                    : 'pointer-events-none border-[#2A2A2A] text-muted-foreground opacity-60'
                }`}
              >
                Abrir WhatsApp manual
              </a>
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
            <p className="mt-2 text-lg font-bold text-white">
              {whatsAppLoading ? 'Carregando...' : whatsAppIntegration?.connected ? 'Conectado' : 'Desconectado'}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {whatsAppIntegration?.businessName || 'Nenhuma conta WhatsApp Business conectada ainda.'}
            </p>
            {whatsAppIntegration?.displayPhoneNumber && (
              <p className="mt-1 text-sm text-white">{whatsAppIntegration.displayPhoneNumber}</p>
            )}
          </div>
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Webhook</p>
            <p className="mt-2 text-lg font-bold text-white">
              {whatsAppIntegration?.webhookVerified ? 'Verificado' : 'Pendente'}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              O webhook da Meta identifica o tenant pelo phone_number_id salvo na integracao do restaurante.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openWhatsAppEmbeddedSignup}
            disabled={whatsAppConnecting || !whatsAppPlatformConfig?.enabled}
            className="btn-primary flex items-center gap-2"
          >
            <MessageCircleMore className="h-4 w-4" />
            {whatsAppConnecting ? 'Conectando...' : 'Conectar WhatsApp'}
          </button>
          <button
            type="button"
            onClick={disconnectWhatsApp}
            disabled={whatsAppDisconnecting || !whatsAppIntegration?.connected}
            className="rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white transition hover:border-red-500/50 disabled:opacity-50"
          >
            <Unplug className="mr-2 inline h-4 w-4" />
            {whatsAppDisconnecting ? 'Desconectando...' : 'Desconectar'}
          </button>
          <a
            href={whatsAppPlatformConfig?.embeddedSignupDocsUrl || 'https://developers.facebook.com/docs/whatsapp/embedded-signup/'}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white transition hover:border-primary/50"
          >
            <ExternalLink className="mr-2 inline h-4 w-4" />
            Documentacao oficial
          </a>
        </div>

        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-5">
            <h4 className="text-base font-bold text-white">Testar envio</h4>
            <p className="mt-1 text-sm text-muted-foreground">
              Envie uma mensagem de teste usando o numero conectado da propria empresa.
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-[220px,1fr,auto] xl:grid-cols-1 2xl:grid-cols-[220px,1fr,auto]">
              <input
                value={whatsAppTestForm.to}
                onChange={(event) => setWhatsAppTestForm((current) => ({ ...current, to: event.target.value }))}
                className="input"
                placeholder="5511999999999"
              />
              <input
                value={whatsAppTestForm.text}
                onChange={(event) => setWhatsAppTestForm((current) => ({ ...current, text: event.target.value }))}
                className="input"
                placeholder="Mensagem de teste"
              />
              <button
                type="button"
                onClick={testWhatsAppConnection}
                disabled={whatsAppTesting || !whatsAppIntegration?.connected}
                className="btn-primary"
              >
                {whatsAppTesting ? 'Enviando...' : 'Testar envio'}
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="text-base font-bold text-white">Teste de recebimento</h4>
                <p className="mt-1 text-sm text-muted-foreground">
                  Mande uma mensagem para o numero conectado. Se o webhook funcionar, ela aparece abaixo.
                </p>
              </div>
              <button
                type="button"
                onClick={refreshWhatsAppInbox}
                disabled={whatsAppInboxLoading || !whatsAppIntegration?.connected}
                className="rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white transition hover:border-primary/50 disabled:opacity-50"
              >
                {whatsAppInboxLoading ? 'Atualizando...' : 'Atualizar mensagens'}
              </button>
            </div>

            <div className="mt-4 max-h-[420px] space-y-3 overflow-y-auto pr-1">
              {whatsAppMessageLogs.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#2A2A2A] bg-[#121212] p-4 text-sm text-muted-foreground">
                  Nenhuma mensagem capturada ainda. Envie um WhatsApp para o numero conectado e clique em Atualizar mensagens.
                </div>
              ) : (
                whatsAppMessageLogs.map((log) => (
                  <div key={log.id} className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          log.direction === 'INBOUND'
                            ? 'bg-emerald-500/15 text-emerald-300'
                            : 'bg-primary/15 text-primary'
                        }`}>
                          {log.direction === 'INBOUND' ? 'Recebida' : 'Enviada'}
                        </span>
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">
                          {log.messageType || 'texto'}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-white">
                      {log.preview || 'Mensagem sem pre-visualizacao textual.'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span>Status: {log.status || 'sem status'}</span>
                      {log.recipient && <span>Numero: {log.recipient}</span>}
                      {log.providerMessageId && <span>Provider ID: {log.providerMessageId}</span>}
                      {log.errorMessage && <span className="text-red-300">Erro: {log.errorMessage}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSettingsSection;
